import dayjs from 'dayjs'
import { DefaultValues } from 'react-hook-form'
import {
  CasePayload,
  CaseStatus,
  CaseToUser,
  ExternalUserCaseRole,
  ExternalUserRoleRole,
  LawTypesPayload,
  UserRole,
  UserType
} from '../../generated'
import { convertToDate, removeUnderScore, titleize } from '.'
import { CreateCaseCustomInput, UpdateCaseCustomProps } from '../interfaceTypes'
import {
  CASE_STATUS_OPTIONS,
  DUPLICATE_CASE_ROUTE,
  DUPLICATE_CONSULT_ROUTE,
  EXTERNAL_USER_ADVERSARY_CASE_ROLES,
  EXTERNAL_USER_CLIENT_CASE_ROLES
} from '../constants'
import { formatAddress, getCasePartiesByRole } from './legalTeam'

export interface CaseFormContext {
  judgeRoleId: string
  clientRoleId: string
  contactRoleId: string
  allLawTypes: LawTypesPayload['lawTypes']
  currentUserId?: string
}

/** Route of the Create form pre-filled from `caseId` (Duplicate); a consult is duplicated as a consult. */
export const getDuplicateCasePath = (caseId: string, isConsultMatter?: boolean | null) =>
  `${isConsultMatter ? DUPLICATE_CONSULT_ROUTE : DUPLICATE_CASE_ROUTE}/${caseId}`

/**
 * Maps a saved matter onto the matter form (the same shape the Create and Update forms share). Used to seed
 * the Update Matter modal and, through `buildDuplicateCaseValues`, the Create Matter form when duplicating.
 */
export const mapCaseToFormValues = (
  caseDetail: CasePayload['case'],
  { judgeRoleId, clientRoleId, contactRoleId, allLawTypes, currentUserId }: CaseFormContext
) => {
  const {
    caseNumber,
    consultMatterNumber,
    isConsultMatter,
    isInternalMatter,
    court,
    status,
    description,
    docketNo,
    caseToExternalUserToExternalUserRole,
    title,
    caseType,
    caseToUsers,
    billing,
    setting,
    openDate,
    closeDate,
    completedDate,
    caseReferences,
    profitCenter,
    location,
    openedBy,
    ccEmailBillsTo,
    consultAttorney
  } = caseDetail || {}

  const judeUser = caseToExternalUserToExternalUserRole?.find(
    item =>
      item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles &&
      item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role ===
        ExternalUserRoleRole.Judge
  )?.externalUserToExternalUserRole.externalUser

  const client = caseToExternalUserToExternalUserRole
    ?.map(externalUserToExternalUserRole => {
      const clientList = externalUserToExternalUserRole.externalUserToExternalUserRole.externalUser
      const currentRoleId = externalUserToExternalUserRole.externalUserToExternalUserRole.externalUserRoleId
      if (
        (currentRoleId === contactRoleId || currentRoleId === clientRoleId) &&
        externalUserToExternalUserRole.caseRole !== ExternalUserCaseRole.PrimaryClient
      )
        return {
          id: clientList?.id || '',
          email: clientList?.email || '',
          firstName: clientList?.firstName || '',
          lastName: clientList?.lastName || '',
          displayName: clientList?.displayName || '',
          clientNumber: clientList?.clientNumber || '',
          workPhone: clientList?.phoneNumber || '',
          caseRole: externalUserToExternalUserRole.caseRole!,
          isBillParticipant: !!externalUserToExternalUserRole.splitBillShare,
          wantsBillDelivery: !!externalUserToExternalUserRole.billDeliveryMethods?.length,
          splitBillShare: externalUserToExternalUserRole.splitBillShare || undefined,
          userRoleId: externalUserToExternalUserRole.externalUserToExternalUserRole.externalUserRoleId,
          billDeliveryMethods: externalUserToExternalUserRole?.billDeliveryMethods ?? undefined,
          billingAddress: externalUserToExternalUserRole?.billingAddress ?? undefined,
          billingPhoneNumber: externalUserToExternalUserRole?.billingPhoneNumber ?? undefined,
          billingEmail: externalUserToExternalUserRole?.billingEmail ?? undefined,
          defaultBillingAddress: formatAddress(clientList?.addresses)
        }
      return undefined
    })
    ?.filter(Boolean)

  const clientUsers = getCasePartiesByRole(
    caseToExternalUserToExternalUserRole || [],
    EXTERNAL_USER_CLIENT_CASE_ROLES,
    ExternalUserRoleRole.Client,
    billing?.splitBillEnabled
  )
  const adversaryUsers = getCasePartiesByRole(
    caseToExternalUserToExternalUserRole || [],
    EXTERNAL_USER_ADVERSARY_CASE_ROLES,
    ExternalUserRoleRole.Adversary
  )

  // Filter out assigned and creator roles
  const filteredCaseToUsers = (caseToUsers?.filter(item => {
    if (!item) return false

    const isParalegal = item.user?.roles?.[0]?.role === UserRole.Paralegal && item?.userType !== UserType.Creator
    const isAssignedOrCreator = item.userType === UserType.AssignedTo || item.userType === UserType.Creator

    return !(isAssignedOrCreator && !isParalegal)
  }) || []) as CaseToUser[]

  const legalTeam = filteredCaseToUsers.map(item => ({
    userId: item.userId,
    userType: item.user?.roles?.[0]?.role === UserRole.Paralegal ? UserType.Paralegal : item.userType,
    hourlyRate: item.userId ? item.hourlyRate : undefined,
    label: item.user?.firstName + ' ' + item.user?.lastName,
    flabel: titleize(removeUnderScore(item.userType)),
    role: item.user?.roles?.[0]?.role
  }))

  const statusDefaultValue = CASE_STATUS_OPTIONS.find(statusItem => statusItem?.value === status)
  const isStatusClosed = status === CaseStatus.Completed

  const { id: courtId, name: courtName, county } = court || {}
  const { id: judgeId, firstName, lastName, email } = judeUser || {}
  const { id: caseTypeId, lawTypeId } = caseType || {}

  const selectedLawType = allLawTypes?.find(lawType => lawType?.id === lawTypeId)
  const { name: selectedLawTypeName } = selectedLawType || {}
  const { id: selectedLawTypeId } = selectedLawType || {}

  const selectedCaseType = selectedLawType?.caseTypes?.find(caseType => caseType?.id === caseTypeId)
  const { name: selectedCaseTypeName } = selectedCaseType || {}
  const { id: selectedCaseTypeId } = selectedCaseType || {}

  const defaultValues: DefaultValues<UpdateCaseCustomProps> = {
    caseNumber: caseNumber || '',
    consultMatterNumber: consultMatterNumber || '',
    court: { id: courtId || '', name: courtName || '', county: county || '' },
    title,
    isConsultMatter,
    isInternalMatter: isInternalMatter ?? false,
    statusType: statusDefaultValue,
    description,
    docketNo: docketNo ?? '',
    judgeUserRoleId: judgeRoleId,
    judgeUser: { id: judgeId || '', email: email || '', firstName: firstName || '', lastName: lastName || '' },
    judgeUserId: '',
    oldJudgeUserId: judgeId,
    caseTypeId: caseTypeId || '',
    lawType: { id: selectedLawTypeId || '', name: selectedLawTypeName || '' },
    caseType: { id: selectedCaseTypeId || '', name: selectedCaseTypeName || '' },
    openDate: openDate ? convertToDate(openDate) : '',
    closeDate: closeDate ? convertToDate(closeDate) : '',
    completedDate: completedDate ? convertToDate(completedDate) : '',
    billing: {
      billingType: billing?.billingType,
      flatFee: billing?.flatFee,
      retainerAmount: billing?.retainerAmount,
      billingCycle: billing?.billingCycle,
      upfrontFlatFee: billing?.upfrontFlatFee,
      monthlyFlatFee: billing?.monthlyFlatFee,
      billEntireFlatFeeUpfront: billing?.billEntireFlatFeeUpfront,
      contingencyShare: billing?.contingencyShare,
      contingencySettlementAmount: billing?.contingencySettlementAmount,
      billGenerationLimitEnabled: billing?.billGenerationLimitEnabled,
      billGenerationType: billing?.billGenerationType,
      billGenerationAmount: billing?.billGenerationAmount,
      billGenerationHours: billing?.billGenerationHours,
      discountAmount: billing?.discountAmount,
      discountPercentage: billing?.discountPercentage,
      discountType: billing?.discountType,
      writeOffInterest: billing?.writeOffInterest,
      increaseInterestRateOnFirstLatePayment: billing?.increaseInterestRateOnFirstLatePayment ?? false,
      splitBillEnabled: billing?.splitBillEnabled,
      consultationFee: billing?.consultationFee,
      retainerAccountId: billing?.retainerAccount?.id,
      pauseBillingUntil: billing?.pauseBillingUntil
        ? new Date(billing.pauseBillingUntil).toISOString().split('T')[0]
        : billing?.pauseBillingUntil,
      interestRates: billing?.interestRates?.map(item => ({
        rate: item.rate,
        applicableDays: item.applicableDays
      })),
      billApproverId: billing?.billApprover?.id,
      skipDeptHeadApproval: billing?.skipDeptHeadApproval,
      billingCycleCustomDate: billing?.billingCycleCustomDate,
      minimumFundsThreshold: billing?.minimumFundsThreshold,
      specialPayments: billing?.specialPayments?.map(item => ({
        amount: item.amount,
        date: new Date(item.date).toISOString().split('T')[0]
      })),
      paymentPlanStartDate: billing?.paymentPlanStartDate
        ? new Date(billing.paymentPlanStartDate).toISOString().split('T')[0]
        : billing?.paymentPlanStartDate
    },
    setting: {
      notifyOnFlatFeeHoursThreshold: !!setting?.flatFeeHoursNotificationThreshold,
      sendFlatFeeHoursExhaustedNotification: setting?.sendFlatFeeHoursExhaustedNotification,
      flatFeeHoursNotificationThreshold: setting?.flatFeeHoursNotificationThreshold
    },
    legalTeam,
    references: caseReferences?.length
      ? caseReferences.map(reference => ({
          percentage: reference?.percentage,
          referenceId: reference?.reference.id
        }))
      : [],
    clientUsers: client?.filter(user => user !== null),
    client: clientUsers.find(primaryClient => primaryClient?.caseRole === ExternalUserCaseRole.PrimaryClient),
    adversaryUsers: adversaryUsers.filter(adversary => adversary?.caseRole !== ExternalUserCaseRole.PrimaryAdversary),
    adversary: adversaryUsers.find(adversary => adversary?.caseRole === ExternalUserCaseRole.PrimaryAdversary),
    profitCenter: {
      name: profitCenter?.name,
      id: profitCenter?.id
    },
    location: {
      name: location?.name,
      id: location?.id
    },
    openedBy: openedBy?.id ?? currentUserId,
    ccEmailBillsTo: ccEmailBillsTo ?? '',
    // Null (not the current user) when unset: unlike Opened By this must never be silently defaulted to
    // whoever happens to be editing, or the backfill would be quietly overwritten with wrong data.
    consultAttorneyId: consultAttorney?.id ?? null
  }

  return { defaultValues, isStatusClosed, selectedLawTypeId, isConsultMatter }
}

/**
 * Seeds the Create Matter form from an existing (active or closed) matter. The copy is a brand-new matter that
 * goes through the normal `createCase` flow, so everything tied to the source matter's identity, lifecycle or
 * history is left out: it gets a fresh matter number, a blank docket number (unique per matter), opens today as
 * active, and drops the one-off billing dates and settlement. Bills, entries, payments, notes, tasks, events and
 * documents are never part of the form, so they are not copied either.
 */
export const buildDuplicateCaseValues = (
  caseDetail: CasePayload['case'],
  context: CaseFormContext
): DefaultValues<CreateCaseCustomInput> => {
  const { defaultValues } = mapCaseToFormValues(caseDetail, context)
  const {
    // Edit-only fields the create form has no use for.
    statusType,
    oldJudgeUserId,
    caseTypeId,
    billing,
    ...copied
  } = defaultValues

  const {
    pauseBillingUntil,
    paymentPlanStartDate,
    specialPayments,
    contingencySettlementAmount,
    ...copiedBilling
  } = billing || {}

  // The two form types disagree on option shapes ({ id, label } vs { id, name }), but the step components both
  // forms render read `name`, which is what the mapper produces.
  return {
    ...(copied as DefaultValues<CreateCaseCustomInput>),
    caseNumber: '',
    consultMatterNumber: '',
    docketNo: '',
    openDate: dayjs().format('YYYY-MM-DDTHH:mm:ssZ'),
    closeDate: null,
    completedDate: null,
    openedBy: context.currentUserId,
    billing: copiedBilling as DefaultValues<CreateCaseCustomInput>['billing']
  }
}
